/**
 * DungeonScene.ts - Dungeon exploration with tile-based rendering
 * Handles movement, stairs, treasure chests, encounters, and boss battles
 */

import { Scene, GameInput, GameKey } from './types/engine';
import { Renderer } from './Renderer';
import { TileMap } from './TileMap';
import { SpriteManager } from './SpriteManager';
import { CharacterSprite, Direction } from './CharacterSprite';
import {
  Dungeon,
  DungeonFloor,
  DungeonTileType,
  DungeonStairs,
  DungeonNPC,
  getDungeon,
  isDungeonTileWalkable,
  getDungeonTileColor,
} from './data/DungeonData';
import {
  Treasure,
  treasureTracker,
} from './data/TreasureData';

interface PlayerState {
  x: number;
  y: number;
  tileX: number;
  tileY: number;
  facing: 'up' | 'down' | 'left' | 'right';
  moving: boolean;
  moveProgress: number;
  animFrame: number;
  animTimer: number;
}

export interface DungeonSceneConfig {
  dungeonId: string;
  startFloor?: number;
  startX?: number;
  startY?: number;
}

export class DungeonScene implements Scene {
  private renderer: Renderer;
  private tileMap: TileMap;
  private spriteManager: SpriteManager;

  // Dungeon state
  private dungeon: Dungeon;
  private currentFloor: number;
  private currentFloorData: DungeonFloor;

  // Player state
  private player: PlayerState;
  private characterSprite: CharacterSprite;
  private readonly TILE_SIZE = 16;
  private readonly MOVE_SPEED = 4; // pixels per frame
  private readonly ANIM_SPEED = 150; // ms per frame

  // Encounter tracking
  private stepCounter: number = 0;
  private stepsToEncounter: number = 0;
  private encounterCallback: (() => void) | null = null;

  // Input cooldown
  private inputCooldown: number = 0;
  private readonly INPUT_COOLDOWN_MS = 100;

  // Callbacks
  private onTreasureOpen: ((treasure: Treasure) => void) | null = null;
  private onTrappedChest: ((enemies: number[], treasure: Treasure) => void) | null = null;
  private onBossEncounter: ((npc: DungeonNPC) => void) | null = null;
  private onStairTransition: ((targetFloor: number) => void) | null = null;
  private onExitDungeon: (() => void) | null = null;

  // Pending treasure (for awarding after trapped chest battle)
  private pendingTreasure: Treasure | null = null;

  // Treasure popup display
  private treasurePopup: { treasure: Treasure; timer: number } | null = null;
  private readonly TREASURE_POPUP_DURATION = 2500; // ms

  // Animation timing
  private globalTime: number = 0;

  // Flash effect (for encounters)
  private flashTime: number = 0;
  private flashColor: string = '#ffffff';

  constructor(
    ctx: CanvasRenderingContext2D,
    spriteManager: SpriteManager,
    config: DungeonSceneConfig
  ) {
    this.renderer = new Renderer(ctx);
    this.spriteManager = spriteManager;
    this.tileMap = new TileMap(spriteManager, 'dungeon_tileset', this.TILE_SIZE);

    // Load dungeon
    const dungeon = getDungeon(config.dungeonId);
    if (!dungeon) {
      throw new Error(`Dungeon not found: ${config.dungeonId}`);
    }
    this.dungeon = dungeon;

    // Set starting floor
    this.currentFloor = config.startFloor ?? dungeon.entryFloor;
    const floorData = dungeon.floors[this.currentFloor - 1];
    if (!floorData) {
      throw new Error(`Floor ${this.currentFloor} not found in dungeon ${config.dungeonId}`);
    }
    this.currentFloorData = floorData;

    // Initialize player
    this.player = {
      x: 0,
      y: 0,
      tileX: config.startX ?? dungeon.entryX,
      tileY: config.startY ?? dungeon.entryY,
      facing: 'down',
      moving: false,
      moveProgress: 0,
      animFrame: 0,
      animTimer: 0,
    };

    // Create character sprite
    this.characterSprite = new CharacterSprite(spriteManager, {
      characterClass: 'FIGHTER',
      x: this.player.tileX * this.TILE_SIZE,
      y: this.player.tileY * this.TILE_SIZE,
      tileSize: this.TILE_SIZE,
    });

    // Initialize tile map
    this.loadFloor(this.currentFloor);
    this.resetEncounterSteps();
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    // Initialize sprites
    this.spriteManager.initializeWithProceduralSprites();

    // Update player position
    this.updatePlayerPixelPosition();
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );

    // Center camera on player
    this.tileMap.centerOn(this.player.x, this.player.y);

    // Reset state
    this.globalTime = 0;
    this.flashTime = 0;

    console.log(
      `[DungeonScene] Entered ${this.dungeon.name} - Floor ${this.currentFloor}`
    );
  }

  update(deltaTime: number): void {
    // Update global time
    this.globalTime += deltaTime;

    // Update flash effect
    if (this.flashTime > 0) {
      this.flashTime -= deltaTime;
    }

    // Update input cooldown
    if (this.inputCooldown > 0) {
      this.inputCooldown -= deltaTime;
    }

    // Update movement
    if (this.player.moving) {
      this.updateMovement(deltaTime);
    }

    // Update walk animation
    this.player.animTimer += deltaTime;
    if (this.player.animTimer >= this.ANIM_SPEED) {
      this.player.animTimer = 0;
      this.player.animFrame = (this.player.animFrame + 1) % 4;
    }

    // Update character sprite
    this.characterSprite.update(deltaTime);

    // Update treasure popup timer
    if (this.treasurePopup) {
      this.treasurePopup.timer -= deltaTime;
      if (this.treasurePopup.timer <= 0) {
        this.treasurePopup = null;
      }
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Clear with dungeon ambient color
    const ambientColor = this.currentFloorData.ambientColor ?? '#000000';
    this.renderer.clear(ambientColor);

    // Render dungeon tiles
    this.renderDungeon(ctx);

    // Render stairs
    this.renderStairs(ctx);

    // Render treasure chests
    this.renderTreasureChests(ctx);

    // Render NPCs/bosses
    this.renderNPCs(ctx);

    // Render player
    this.renderPlayer(ctx);

    // Render exit indicator (if on entry floor)
    this.renderExitIndicator(ctx);

    // Render flash effect (encounters)
    if (this.flashTime > 0) {
      const alpha = Math.min(1, this.flashTime / 100);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = this.flashColor;
      ctx.fillRect(0, 0, 256, 240);
      ctx.restore();
    }

    // Render UI overlay
    this.renderUI(ctx);

    // Render treasure popup (on top of everything)
    this.renderTreasurePopup(ctx);

    // Debug info
    // if (process.env.NODE_ENV === 'development') {
    //   this.renderDebugInfo(ctx);
    // }
  }

  handleInput(input: GameInput): void {
    // Don't process input while moving or on cooldown
    if (this.player.moving || this.inputCooldown > 0) return;

    // Check for menu button - let parent handle menu opening
    if (input.justPressed.has(GameKey.START)) {
      // Menu opening is handled by GameEngine
      return;
    }

    // Handle movement
    let dx = 0;
    let dy = 0;
    let newFacing: 'up' | 'down' | 'left' | 'right' | null = null;

    if (input.pressed.has(GameKey.UP)) {
      dy = -1;
      newFacing = 'up';
    } else if (input.pressed.has(GameKey.DOWN)) {
      dy = 1;
      newFacing = 'down';
    } else if (input.pressed.has(GameKey.LEFT)) {
      dx = -1;
      newFacing = 'left';
    } else if (input.pressed.has(GameKey.RIGHT)) {
      dx = 1;
      newFacing = 'right';
    }

    if (newFacing) {
      this.player.facing = newFacing;
    }

    if (dx !== 0 || dy !== 0) {
      this.tryMove(dx, dy);
    }

    // Action button (A/Z) - interact with chests, NPCs, stairs, and exit
    if (input.justPressed.has(GameKey.A)) {
      // Try to exit first if on entry tile, otherwise interact
      if (!this.tryExit()) {
        this.tryInteract();
      }
    }
  }

  cleanup(): void {
    // Nothing to cleanup
  }

  // =========================================================================
  // Floor Management
  // =========================================================================

  private loadFloor(floorNumber: number): void {
    const floorData = this.dungeon.floors[floorNumber - 1];
    if (!floorData) {
      console.error(`Floor ${floorNumber} not found in dungeon`);
      return;
    }

    this.currentFloor = floorNumber;
    this.currentFloorData = floorData;

    // Set tile map data
    this.tileMap.setMap({
      width: floorData.width,
      height: floorData.height,
      tiles: floorData.tiles,
    });

    // Update viewport dimensions
    this.tileMap.width = floorData.width;
    this.tileMap.height = floorData.height;

    // Configure walkable tiles
    const walkableTiles: number[] = [];
    for (let i = 0; i <= 16; i++) {
      if (isDungeonTileWalkable(i as DungeonTileType)) {
        walkableTiles.push(i);
      }
    }
    this.tileMap.setWalkableTiles(walkableTiles);

    console.log(`[DungeonScene] Loaded floor ${floorNumber}`);
  }

  private transitionToFloor(
    targetFloor: number,
    targetX: number,
    targetY: number
  ): void {
    // Load new floor
    this.loadFloor(targetFloor);

    // Move player to target position
    this.player.tileX = targetX;
    this.player.tileY = targetY;
    this.updatePlayerPixelPosition();

    // Update character sprite
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );

    // Center camera
    this.tileMap.centerOn(this.player.x, this.player.y);

    // Reset encounters
    this.resetEncounterSteps();

    // Trigger callback
    if (this.onStairTransition) {
      this.onStairTransition(targetFloor);
    }

    console.log(
      `[DungeonScene] Transitioned to floor ${targetFloor} at (${targetX}, ${targetY})`
    );
  }

  // =========================================================================
  // Movement
  // =========================================================================

  private tryMove(dx: number, dy: number): void {
    const newTileX = this.player.tileX + dx;
    const newTileY = this.player.tileY + dy;

    // Check bounds
    if (
      newTileX < 0 ||
      newTileX >= this.currentFloorData.width ||
      newTileY < 0 ||
      newTileY >= this.currentFloorData.height
    ) {
      return;
    }

    // Check if walkable
    const tile = this.currentFloorData.tiles[newTileY]?.[newTileX];
    if (tile === undefined || !isDungeonTileWalkable(tile)) {
      return;
    }

    // Determine direction for character sprite
    const direction: Direction =
      dx < 0 ? 'left' : dx > 0 ? 'right' : dy < 0 ? 'up' : 'down';

    // Start movement
    this.player.tileX = newTileX;
    this.player.tileY = newTileY;
    this.player.moving = true;
    this.player.moveProgress = 0;
    this.inputCooldown = this.INPUT_COOLDOWN_MS;

    // Update character sprite movement
    const targetX = newTileX * this.TILE_SIZE;
    const targetY = newTileY * this.TILE_SIZE;
    this.characterSprite.moveTo(targetX, targetY, direction);

    // Increment step counter and check for encounters
    this.stepCounter++;
    const encounterRate = this.currentFloorData.encounterRate;
    if (this.stepCounter >= this.stepsToEncounter && encounterRate > 0) {
      this.triggerEncounter();
    }

    // Check for automatic stair transition
    const stairs = this.getStairsAt(newTileX, newTileY);
    if (stairs) {
      // Transition after movement completes
      setTimeout(() => {
        this.handleStairs(stairs);
      }, (this.TILE_SIZE / this.MOVE_SPEED) * 16);
    }
  }

  private updateMovement(_deltaTime: number): void {
    this.player.moveProgress += this.MOVE_SPEED;

    if (this.player.moveProgress >= this.TILE_SIZE) {
      // Movement complete
      this.player.moving = false;
      this.player.moveProgress = 0;
      this.updatePlayerPixelPosition();

      // Center camera on player
      this.tileMap.centerOn(this.player.x, this.player.y);
    } else {
      // Interpolate position
      this.updatePlayerPixelPosition();
    }
  }

  private updatePlayerPixelPosition(): void {
    this.player.x = this.player.tileX * this.TILE_SIZE + this.TILE_SIZE / 2;
    this.player.y = this.player.tileY * this.TILE_SIZE + this.TILE_SIZE / 2;
  }

  // =========================================================================
  // Interaction
  // =========================================================================

  private tryInteract(): void {
    // Check for stairs
    const stairs = this.getStairsAt(this.player.tileX, this.player.tileY);
    if (stairs) {
      this.handleStairs(stairs);
      return;
    }

    // Check for treasure chest
    const treasure = treasureTracker.getUnopenedTreasureAt(
      this.dungeon.id,
      this.currentFloor,
      this.player.tileX,
      this.player.tileY
    );
    if (treasure) {
      this.handleTreasure(treasure);
      return;
    }

    // Check for NPC/boss
    const npc = this.getNPCAt(this.player.tileX, this.player.tileY);
    if (npc) {
      this.handleNPC(npc);
      return;
    }
  }

  private tryExit(): boolean {
    // Check if player is on entry tile
    if (
      this.currentFloor === this.dungeon.entryFloor &&
      this.player.tileX === this.dungeon.entryX &&
      this.player.tileY === this.dungeon.entryY
    ) {
      if (this.onExitDungeon) {
        this.onExitDungeon();
        return true;
      }
    }
    return false;
  }

  private handleStairs(stairs: DungeonStairs): void {
    const targetX = stairs.targetX ?? this.player.tileX;
    const targetY = stairs.targetY ?? this.player.tileY;

    this.transitionToFloor(stairs.targetFloor, targetX, targetY);
  }

  private handleTreasure(treasure: Treasure): void {
    // Mark as opened
    treasureTracker.openChest(treasure.id);
    treasureTracker.save();

    // Check if trapped
    if (treasure.trapped && treasure.trapEnemyGroup && treasure.trapEnemyGroup.length > 0) {
      console.log(`[DungeonScene] Chest trapped! Triggering battle with enemies:`, treasure.trapEnemyGroup);

      // Store treasure for awarding after battle
      this.pendingTreasure = treasure;

      // Trigger trapped chest battle callback
      if (this.onTrappedChest) {
        this.onTrappedChest(treasure.trapEnemyGroup, treasure);
        return; // Don't award treasure yet - wait for battle victory
      }
    }

    // Award treasure immediately if not trapped (or no callback)
    this.awardTreasure(treasure);
  }

  /**
   * Award treasure to player (called after opening or after winning trapped chest battle)
   */
  private awardTreasure(treasure: Treasure): void {
    // Trigger callback
    if (this.onTreasureOpen) {
      this.onTreasureOpen(treasure);
    }

    // Show popup
    this.showTreasurePopup(treasure);

    console.log(`[DungeonScene] Awarded treasure: ${treasure.name}`);
  }

  /**
   * Award pending treasure after winning trapped chest battle
   * Called from Game.tsx after battle victory
   */
  awardPendingTreasure(): void {
    if (this.pendingTreasure) {
      this.awardTreasure(this.pendingTreasure);
      this.pendingTreasure = null;
    }
  }

  /**
   * Show treasure popup notification
   */
  private showTreasurePopup(treasure: Treasure): void {
    this.treasurePopup = {
      treasure,
      timer: this.TREASURE_POPUP_DURATION,
    };
  }

  /**
   * Render treasure popup notification
   */
  private renderTreasurePopup(ctx: CanvasRenderingContext2D): void {
    if (!this.treasurePopup) return;

    const { treasure, timer } = this.treasurePopup;

    // Calculate fade (fade in for first 200ms, fade out for last 500ms)
    let alpha = 1;
    const fadeInDuration = 200;
    const fadeOutDuration = 500;
    const elapsed = this.TREASURE_POPUP_DURATION - timer;

    if (elapsed < fadeInDuration) {
      alpha = elapsed / fadeInDuration;
    } else if (timer < fadeOutDuration) {
      alpha = timer / fadeOutDuration;
    }

    ctx.save();
    ctx.globalAlpha = alpha;

    // Popup background
    const popupWidth = 180;
    const popupHeight = 50;
    const popupX = (256 - popupWidth) / 2;
    const popupY = 80;

    // Background with gold border
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.fillRect(popupX, popupY, popupWidth, popupHeight);

    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.strokeRect(popupX, popupY, popupWidth, popupHeight);

    // Inner gold highlight
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(popupX + 3, popupY + 3, popupWidth - 6, popupHeight - 6);

    // "Found:" header
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 8px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Found:', 128, popupY + 15);

    // Treasure name (gold)
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 10px sans-serif';
    ctx.fillText(treasure.name, 128, popupY + 30);

    // Treasure description (smaller, gray)
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '7px sans-serif';
    ctx.fillText(treasure.description, 128, popupY + 43);

    // Treasure icon based on type
    let icon = '📦';
    switch (treasure.type) {
      case 'GIL':
        icon = '💰';
        break;
      case 'ITEM':
        icon = '🧪';
        break;
      case 'EQUIPMENT':
        icon = '⚔️';
        break;
      case 'KEY_ITEM':
        icon = '🔑';
        break;
      case 'SPELL':
        icon = '✨';
        break;
    }

    ctx.font = '16px sans-serif';
    ctx.fillText(icon, popupX + 15, popupY + 32);

    ctx.restore();
  }

  private handleNPC(npc: DungeonNPC): void {
    if (npc.isBoss) {
      console.log(`[DungeonScene] Boss encounter: ${npc.name}`);
      if (this.onBossEncounter) {
        this.onBossEncounter(npc);
      }
    } else {
      console.log(`[DungeonScene] NPC interaction: ${npc.name}`);
      // TODO: Show dialogue
    }
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private getStairsAt(x: number, y: number): DungeonStairs | null {
    return (
      this.currentFloorData.stairs.find(
        (stairs) => stairs.x === x && stairs.y === y
      ) ?? null
    );
  }

  private getNPCAt(x: number, y: number): DungeonNPC | null {
    if (!this.currentFloorData.npcs) return null;
    return (
      this.currentFloorData.npcs.find((npc) => npc.x === x && npc.y === y) ??
      null
    );
  }

  // =========================================================================
  // Encounters
  // =========================================================================

  private resetEncounterSteps(): void {
    // Random number of steps until next encounter based on encounter rate
    const baseSteps = 15;
    const variance = 10;
    const rate = this.currentFloorData.encounterRate / 100;

    this.stepsToEncounter = Math.floor(
      baseSteps / (rate || 0.1) + Math.random() * variance
    );
    this.stepCounter = 0;
  }

  private triggerEncounter(): void {
    // Flash screen for encounter effect
    this.startFlash('#ffffff');

    // Trigger battle after a brief delay
    setTimeout(() => {
      if (this.encounterCallback) {
        this.encounterCallback();
      }
    }, 100);

    this.resetEncounterSteps();
  }

  private startFlash(color: string): void {
    this.flashColor = color;
    this.flashTime = 200; // ms
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  private renderDungeon(_ctx: CanvasRenderingContext2D): void {
    const viewport = this.tileMap.viewport;
    const startCol = Math.floor(viewport.x / this.TILE_SIZE);
    const endCol = Math.ceil((viewport.x + viewport.width) / this.TILE_SIZE);
    const startRow = Math.floor(viewport.y / this.TILE_SIZE);
    const endRow = Math.ceil((viewport.y + viewport.height) / this.TILE_SIZE);

    // Clamp to floor bounds
    const clampedStartCol = Math.max(0, startCol);
    const clampedEndCol = Math.min(this.currentFloorData.width, endCol);
    const clampedStartRow = Math.max(0, startRow);
    const clampedEndRow = Math.min(this.currentFloorData.height, endRow);

    // Render tiles
    for (let row = clampedStartRow; row < clampedEndRow; row++) {
      for (let col = clampedStartCol; col < clampedEndCol; col++) {
        const tile = this.currentFloorData.tiles[row]?.[col];
        if (tile === undefined) continue;

        const screenPos = this.tileMap.worldToScreen(
          col * this.TILE_SIZE,
          row * this.TILE_SIZE
        );

        // Try to get sprite
        const sprite = this.spriteManager.getSprite('dungeon_tileset', tile);

        if (sprite) {
          this.renderer.drawSprite(sprite, screenPos.x, screenPos.y);
        } else {
          // Fallback to colored rectangle
          const color = getDungeonTileColor(tile);
          this.renderer.drawRect(
            screenPos.x,
            screenPos.y,
            this.TILE_SIZE,
            this.TILE_SIZE,
            color,
            true
          );
        }
      }
    }
  }

  private renderStairs(_ctx: CanvasRenderingContext2D): void {
    this.currentFloorData.stairs.forEach((stairs) => {
      const screenPos = this.tileMap.worldToScreen(
        stairs.x * this.TILE_SIZE,
        stairs.y * this.TILE_SIZE
      );

      // Only render if on screen
      if (
        screenPos.x < -20 ||
        screenPos.x > 280 ||
        screenPos.y < -20 ||
        screenPos.y > 260
      ) {
        return;
      }

      // Draw stairs indicator
      const color = stairs.direction === 'up' ? '#8888ff' : '#ff8888';
      this.renderer.drawRect(
        screenPos.x + 2,
        screenPos.y + 2,
        this.TILE_SIZE - 4,
        this.TILE_SIZE - 4,
        color,
        true
      );

      // Draw arrow
      const arrow = stairs.direction === 'up' ? '↑' : '↓';
      this.renderer.drawText(
        arrow,
        screenPos.x + this.TILE_SIZE / 2,
        screenPos.y + this.TILE_SIZE / 2 + 3,
        {
          font: 'ff1',
          fontSize: 12,
          color: '#ffffff',
          align: 'center',
        }
      );
    });
  }

  private renderTreasureChests(_ctx: CanvasRenderingContext2D): void {
    // Iterate through all positions looking for treasure
    for (let y = 0; y < this.currentFloorData.height; y++) {
      for (let x = 0; x < this.currentFloorData.width; x++) {
        const treasure = treasureTracker.getTreasureAt(
          this.dungeon.id,
          this.currentFloor,
          x,
          y
        );

        if (treasure) {
          const screenPos = this.tileMap.worldToScreen(
            x * this.TILE_SIZE,
            y * this.TILE_SIZE
          );

          // Only render if on screen
          if (
            screenPos.x < -20 ||
            screenPos.x > 280 ||
            screenPos.y < -20 ||
            screenPos.y > 260
          ) {
            continue;
          }

          // Check if opened
          const isOpened = treasureTracker.isOpened(treasure.id);
          const color = isOpened ? '#886644' : '#ffcc00';

          // Draw chest
          this.renderer.drawRect(
            screenPos.x + 2,
            screenPos.y + 4,
            this.TILE_SIZE - 4,
            this.TILE_SIZE - 6,
            color,
            true
          );
          this.renderer.drawRect(
            screenPos.x + 2,
            screenPos.y + 4,
            this.TILE_SIZE - 4,
            this.TILE_SIZE - 6,
            '#000000',
            false
          );

          // Draw lock if closed
          if (!isOpened) {
            this.renderer.drawRect(
              screenPos.x + 6,
              screenPos.y + 8,
              4,
              4,
              '#000000',
              true
            );
          }
        }
      }
    }
  }

  private renderNPCs(_ctx: CanvasRenderingContext2D): void {
    if (!this.currentFloorData.npcs) return;

    this.currentFloorData.npcs.forEach((npc) => {
      const screenPos = this.tileMap.worldToScreen(
        npc.x * this.TILE_SIZE,
        npc.y * this.TILE_SIZE
      );

      // Only render if on screen
      if (
        screenPos.x < -20 ||
        screenPos.x > 280 ||
        screenPos.y < -20 ||
        screenPos.y > 260
      ) {
        return;
      }

      // Draw NPC/boss marker
      const color = npc.isBoss ? '#ff0000' : '#00ff00';
      this.renderer.drawRect(
        screenPos.x + 2,
        screenPos.y + 2,
        this.TILE_SIZE - 4,
        this.TILE_SIZE - 4,
        color,
        true
      );
      this.renderer.drawRect(
        screenPos.x + 2,
        screenPos.y + 2,
        this.TILE_SIZE - 4,
        this.TILE_SIZE - 4,
        '#000000',
        false
      );

      // Draw name if close to player
      const dx = Math.abs(npc.x - this.player.tileX);
      const dy = Math.abs(npc.y - this.player.tileY);
      if (dx <= 2 && dy <= 2) {
        this.renderer.drawText(
          npc.name,
          screenPos.x + this.TILE_SIZE / 2,
          screenPos.y - 4,
          {
            font: 'ff1',
            fontSize: 8,
            color: color,
            align: 'center',
            shadow: true,
          }
        );
      }
    });
  }

  private renderPlayer(_ctx: CanvasRenderingContext2D): void {
    const screenPos = this.tileMap.worldToScreen(
      this.player.x - 8,
      this.player.y - 8
    );

    // Render character sprite with shadow
    this.characterSprite.renderWithShadow(this.renderer, screenPos.x, screenPos.y);
  }

  private renderExitIndicator(ctx: CanvasRenderingContext2D): void {
    // Only show exit indicator on entry floor
    if (this.currentFloor !== this.dungeon.entryFloor) return;

    const entryX = this.dungeon.entryX;
    const entryY = this.dungeon.entryY;

    // Calculate screen position
    const screenX = entryX * this.TILE_SIZE - this.tileMap.viewport.x + this.TILE_SIZE / 2;
    const screenY = entryY * this.TILE_SIZE - this.tileMap.viewport.y + this.TILE_SIZE / 2;

    // Check if exit is on screen
    if (screenX < -16 || screenX > 272 || screenY < -16 || screenY > 256) return;

    // Pulsing glow effect
    const pulse = Math.sin(this.globalTime / 400) * 0.4 + 0.6;

    ctx.save();

    // Draw pulsing circle
    ctx.fillStyle = `rgba(0, 255, 100, ${pulse * 0.5})`;
    ctx.beginPath();
    ctx.arc(screenX, screenY, 10 + pulse * 3, 0, Math.PI * 2);
    ctx.fill();

    // Inner circle
    ctx.fillStyle = `rgba(100, 255, 150, ${pulse})`;
    ctx.beginPath();
    ctx.arc(screenX, screenY, 4, 0, Math.PI * 2);
    ctx.fill();

    // "EXIT" text above
    ctx.fillStyle = `rgba(0, 255, 100, ${pulse})`;
    ctx.font = 'bold 6px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('EXIT', screenX, screenY - 14);

    ctx.restore();
  }

  private renderUI(_ctx: CanvasRenderingContext2D): void {
    // Render dungeon name and floor at top
    this.renderer.drawText(
      `${this.dungeon.name} - ${this.currentFloor}F`,
      128,
      8,
      {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
        align: 'center',
        shadow: true,
      }
    );

    // Render encounter warning
    if (this.currentFloorData.encounterRate > 0) {
      const dangerLevel = this.stepCounter / this.stepsToEncounter;
      if (dangerLevel > 0.7) {
        const alpha = Math.floor((Math.sin(this.globalTime / 200) + 1) * 127);
        const color = `rgba(255, 0, 0, ${alpha / 255})`;
        this.renderer.drawText('!', 240, 120, {
          font: 'ff1',
          fontSize: 16,
          color: color,
          align: 'center',
        });
      }
    }

    // Show exit prompt when standing on entry tile
    if (this.currentFloor === this.dungeon.entryFloor &&
        this.player.tileX === this.dungeon.entryX &&
        this.player.tileY === this.dungeon.entryY) {
      this.renderer.drawText('Press Z to Exit', 128, 220, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ff88',
        align: 'center',
        shadow: true,
      });
    }
  }

  private renderDebugInfo(_ctx: CanvasRenderingContext2D): void {
    const tile = this.currentFloorData.tiles[this.player.tileY]?.[this.player.tileX];
    const info = [
      `Floor: ${this.currentFloor}/${this.dungeon.floors.length}`,
      `Pos: (${this.player.tileX}, ${this.player.tileY})`,
      `Tile: ${DungeonTileType[tile ?? 0]}`,
      `Steps: ${this.stepCounter}/${this.stepsToEncounter}`,
    ];

    info.forEach((text, i) => {
      this.renderer.drawText(text, 2, 2 + i * 10, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ff00',
        shadow: true,
      });
    });
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Set player position (in tiles)
   */
  setPosition(x: number, y: number): void {
    this.player.tileX = Math.max(
      0,
      Math.min(x, this.currentFloorData.width - 1)
    );
    this.player.tileY = Math.max(
      0,
      Math.min(y, this.currentFloorData.height - 1)
    );

    this.updatePlayerPixelPosition();
    this.tileMap.centerOn(this.player.x, this.player.y);

    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );
  }

  /**
   * Set character class for player sprite
   */
  setCharacterClass(characterClass: string): void {
    this.characterSprite.setCharacterClass(characterClass);
  }

  /**
   * Get player position
   */
  getPosition(): { x: number; y: number } {
    return {
      x: this.player.tileX,
      y: this.player.tileY,
    };
  }

  /**
   * Get current floor number
   */
  getCurrentFloor(): number {
    return this.currentFloor;
  }

  /**
   * Get dungeon info
   */
  getDungeonInfo(): { id: string; name: string; floor: number } {
    return {
      id: this.dungeon.id,
      name: this.dungeon.name,
      floor: this.currentFloor,
    };
  }

  /**
   * Set encounter callback
   */
  onEncounter(callback: () => void): void {
    this.encounterCallback = callback;
  }

  /**
   * Set treasure open callback
   */
  onTreasure(callback: (treasure: Treasure) => void): void {
    this.onTreasureOpen = callback;
  }

  /**
   * Set trapped chest callback (for triggering battles with trap enemies)
   */
  onTrappedChestBattle(callback: (enemies: number[], treasure: Treasure) => void): void {
    this.onTrappedChest = callback;
  }

  /**
   * Set boss encounter callback
   */
  onBoss(callback: (npc: DungeonNPC) => void): void {
    this.onBossEncounter = callback;
  }

  /**
   * Set stair transition callback
   */
  onStairs(callback: (targetFloor: number) => void): void {
    this.onStairTransition = callback;
  }

  /**
   * Set exit dungeon callback
   */
  onExit(callback: () => void): void {
    this.onExitDungeon = callback;
  }

  /**
   * Force an encounter
   */
  startEncounter(): void {
    if (this.encounterCallback) {
      this.encounterCallback();
    }
  }

  /**
   * Transition to specific floor
   */
  goToFloor(floor: number, x?: number, y?: number): void {
    const targetX = x ?? this.dungeon.entryX;
    const targetY = y ?? this.dungeon.entryY;
    this.transitionToFloor(floor, targetX, targetY);
  }

  /**
   * Load a new dungeon (for reusing scene instance)
   */
  loadDungeon(dungeonId: string): void {
    const dungeon = getDungeon(dungeonId);
    if (!dungeon) {
      console.error(`[DungeonScene] Dungeon not found: ${dungeonId}`);
      return;
    }

    console.log(`[DungeonScene] Loading dungeon: ${dungeon.name}`);
    this.dungeon = dungeon;

    // Set starting floor
    this.currentFloor = dungeon.entryFloor;
    const floorData = dungeon.floors[this.currentFloor - 1];
    if (!floorData) {
      console.error(`[DungeonScene] Floor ${this.currentFloor} not found in dungeon ${dungeonId}`);
      return;
    }
    this.currentFloorData = floorData;

    // Initialize player at dungeon entry
    this.player.tileX = dungeon.entryX;
    this.player.tileY = dungeon.entryY;
    this.player.facing = 'down';
    this.player.moving = false;
    this.player.moveProgress = 0;

    // Update character sprite position
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );

    // Load the floor tiles
    this.loadFloor(this.currentFloor);

    // Update player pixel position and camera
    this.updatePlayerPixelPosition();
    this.tileMap.centerOn(this.player.x, this.player.y);

    // Reset encounter steps
    this.resetEncounterSteps();
  }
}
